import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import Header from './Header';
import DemoBanner from './DemoBanner';
import OfflineBanner from './OfflineBanner';
import SupportButton from './SupportButton';
import GraceBanner from '@/components/billing/GraceBanner';
import AccessBlocked from '@/components/billing/AccessBlocked';
import { useAccessStatus } from '@/hooks/useAccessStatus';

const LayoutWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.bgBody};
`;

const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: ${({ theme }) => theme.layout.sidebarWidth};
  transition: margin-left ${({ theme }) => theme.transitions.slow};

  @media (max-width: 767.98px) {
    margin-left: 0;
  }
`;

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { isGrace, isBlocked, graceEndsAt } = useAccessStatus();

  // When blocked, swap the page content for the access-blocked screen — but let
  // /settings through so the user can still update payment / reactivate. The
  // surrounding chrome (Sidebar/Header) stays mounted so logout remains reachable.
  const blockContent = isBlocked && location.pathname !== '/settings';

  return (
    <LayoutWrapper>
      <a href="#main-content" className="skip-link">{t('a11y.skipToContent')}</a>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MainArea id="main-content">
        <Header onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />
        <DemoBanner />
        <OfflineBanner />
        {isGrace && <GraceBanner graceEndsAt={graceEndsAt} />}
        {blockContent ? <AccessBlocked /> : <Outlet />}
        <SupportButton />
      </MainArea>
    </LayoutWrapper>
  );
}
